import { Icon, IconButton, Switch, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { getUproColor } from "utils/colors";
import SelectorProductosEvento from "../SelectorProductosEvento";
import { PRECIO_MAXIMO_EVENTO, formatoPesos, productosHabilitados } from "../../utils";

const accion = (titulo, icono, onClick, disabled = false) => (
  <Tooltip title={titulo} placement="top">
    <span>
      <IconButton aria-label={titulo} disabled={disabled} size="small" onClick={onClick} sx={{ color: getUproColor("uproGreen") }}>
        <Icon>{icono}</Icon>
      </IconButton>
    </span>
  </Tooltip>
);

export default function PasoProductos({ form, setField, errores, categorias, idsConVentas, soloLectura }) {
  const [selectorAbierto, setSelectorAbierto] = useState(false);
  const productos = form.productos;
  const vendidos = new Set((idsConVentas || []).map(String));
  const cambiar = (index, campo, valor) =>
    setField("productos", productos.map((item, i) => (i === index ? { ...item, [campo]: valor } : item)));
  const mover = (index, delta) => {
    const siguiente = [...productos];
    [siguiente[index], siguiente[index + delta]] = [siguiente[index + delta], siguiente[index]];
    setField("productos", siguiente);
  };
  const agregar = (nuevos) => {
    setField("productos", [
      ...productos,
      ...nuevos.map((producto) => ({ idProducto: String(producto.id), producto, precioEvento: "", habilitado: true })),
    ]);
    setSelectorAbierto(false);
  };

  return (
    <SoftBox>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <SoftTypography variant="caption" color="secondary">
          {productos.length} producto(s) · {productosHabilitados(form).length} a la venta. Si deja el precio vacío, se usa el de carta.
        </SoftTypography>
        {!soloLectura && (
          <SoftButton color="primary" circular onClick={() => setSelectorAbierto(true)}>
            <Icon>add</Icon>&nbsp;Agregar productos
          </SoftButton>
        )}
      </SoftBox>
      {errores.productos && <SoftTypography variant="caption" color="error" display="block" mb={1}>{errores.productos}</SoftTypography>}

      {productos.length ? (
        <SoftBox sx={{ display: "grid", gap: 1 }}>
          {productos.map((item, index) => {
            const nombre = item.producto?.nombre || `Producto #${item.idProducto}`;
            const vendido = vendidos.has(String(item.idProducto));
            const sinPrecio = (item.precioEvento === "" || item.precioEvento == null) && !(Number(item.producto?.precioCarta) > 0);
            return (
              <SoftBox key={item.idProducto} p={1.5} borderRadius="lg"
                sx={{
                  display: "grid", alignItems: "center", gap: 1.5,
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) 120px minmax(150px, 1fr) auto auto" },
                  border: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}`,
                  opacity: item.habilitado ? 1 : 0.55,
                }}>
                <SoftBox minWidth={0}>
                  <SoftTypography variant="button" fontWeight="bold" display="block">{nombre}</SoftTypography>
                  <SoftTypography variant="caption" color="secondary">{item.producto?.categoriaProducto?.categoria || ""}</SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" color="secondary">
                  Carta: {item.producto?.precioCarta != null ? formatoPesos(item.producto.precioCarta) : "—"}
                </SoftTypography>
                <SoftInput type="number" size="small" value={item.precioEvento} placeholder={sinPrecio ? "Sin precio de carta" : "Precio de carta"}
                  onChange={(e) => cambiar(index, "precioEvento", e.target.value)} error={item.habilitado && sinPrecio} disabled={soloLectura}
                  inputProps={{ min: 0.01, max: PRECIO_MAXIMO_EVENTO, step: 0.01, disabled: soloLectura, "aria-label": `Precio en el evento de ${nombre}` }}
                  icon={{ component: "attach_money", direction: "left" }} />
                <SoftBox display="flex" alignItems="center">
                  <Switch checked={item.habilitado} disabled={soloLectura} onChange={(_, value) => cambiar(index, "habilitado", value)}
                    inputProps={{ "aria-label": `${nombre} a la venta` }} />
                  <SoftTypography variant="caption">{item.habilitado ? "A la venta" : "Pausado"}</SoftTypography>
                </SoftBox>
                {!soloLectura && <SoftBox display="flex" gap={1} justifyContent="flex-end">
                  {accion("Subir", "arrow_upward", () => mover(index, -1), index === 0)}
                  {accion("Bajar", "arrow_downward", () => mover(index, 1), index === productos.length - 1)}
                  {accion(vendido ? "Tiene ventas en el evento: páuselo en lugar de quitarlo" : "Quitar del evento", "delete",
                    () => setField("productos", productos.filter((_, i) => i !== index)), vendido)}
                </SoftBox>}
              </SoftBox>
            );
          })}
        </SoftBox>
      ) : (
        <SinDatos titulo="Sin productos" mensaje="Agregue productos o habilite el menú universitario en el paso siguiente." icono="inventory_2" />
      )}

      <SelectorProductosEvento open={selectorAbierto} categorias={categorias}
        idsExistentes={productos.map((item) => item.idProducto)}
        onCancel={() => setSelectorAbierto(false)} onConfirm={agregar} />
    </SoftBox>
  );
}

PasoProductos.propTypes = {
  form: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired,
  errores: PropTypes.object.isRequired,
  categorias: PropTypes.array.isRequired,
  idsConVentas: PropTypes.arrayOf(PropTypes.string),
  soloLectura: PropTypes.bool,
};

PasoProductos.defaultProps = { idsConVentas: [], soloLectura: false };
